import React from "react";
import { Form, Radio, InputNumber, Rate, Checkbox } from "antd";

/**
 * Renderiza los Form.Item de una plantilla de evaluación según su subtype.
 * Compartido entre la sesión del terapeuta y el modal de evaluación al vuelo.
 */
/**
 * Recibe `t` por parametro y no lo saca de un hook a proposito: esto NO es un
 * componente, es una funcion que llaman dos pantallas distintas para construir
 * los campos del formulario. Un hook aqui seria ilegal.
 */
export function buildAnswerItems(template, t) {
  const subtype = template?.subtype;

  return (template?.questions || []).map((q) => {
    const name = ["q", q.id];
    // Ganchos para la geometria tactil. No cambian nada de lo que se responde
    // ni de lo que se envia: solo permiten que la hoja de estilos convierta las
    // opciones en una rejilla de dianas de 48 px por debajo de 768.
    const clases = "revi-item-clinico";
    const clasesGrupo = (n) => `revi-respuestas revi-respuestas--${n}`;

    if (subtype === "yesavage_gds15") {
      return (
        <Form.Item
          key={q.id}
          className={clases}
          label={q.text}
          name={name}
          rules={[{ required: true, message: t("respuestas.obligatorio") }]}
        >
          {/*
            "Si" y "No" estaban escritos en castellano dentro del codigo. Un
            terapeuta frances pasaba una escala de depresion con las dos
            respuestas en un idioma que su paciente no tiene por que entender, y
            responder mal un item por no entender la etiqueta es un dato clinico
            falso guardado como bueno. Por eso este si se traduce en los dos
            anchos: el valor que viaja (true/false) no cambia.
          */}
          <Radio.Group className={clasesGrupo(2)}>
            <Radio value={true}>{t("respuestas.si")}</Radio>
            <Radio value={false}>{t("respuestas.no")}</Radio>
          </Radio.Group>
        </Form.Item>
      );
    }

    /*
      ESCALAS BINARIAS (Lawton, STAI de 13 items).
      Sin esta rama caian al campo numerico del final: una escala de SI/NO se
      contestaba tecleando un numero, sin que en ninguna parte dijera que 1 es
      si y 0 es no, y sin tope. El calculador suma el valor tal cual
      (`calculateYesNo`), asi que los valores siguen siendo 1 y 0.
    */
    if (subtype === "yes_no") {
      return (
        <Form.Item
          key={q.id}
          className={clases}
          label={q.text}
          name={name}
          rules={[{ required: true, message: t("respuestas.obligatorio") }]}
        >
          <Radio.Group className={clasesGrupo(2)}>
            <Radio value={1}>{t("respuestas.si")}</Radio>
            <Radio value={0}>{t("respuestas.no")}</Radio>
          </Radio.Group>
        </Form.Item>
      );
    }

    /*
      ESCALAS DE FRECUENCIA DE 1 A 3 (I-PANAS-SF, STAI de 8 items).
      Mismo caso: sin rama propia se contestaban a mano. `calculateSimpleSum`
      suma el valor, y el maximo por item de este subtipo es 3.
    */
    if (subtype === "rating_1_3") {
      return (
        <Form.Item
          key={q.id}
          className={clases}
          label={q.text}
          name={name}
          rules={[{ required: true }]}
        >
          <Radio.Group className={clasesGrupo(3)}>
            {[1, 2, 3].map((v) => (
              <Radio key={v} value={v}>
                {v}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      );
    }

    if (subtype === "ryff") {
      return (
        <Form.Item
          key={q.id}
          className={clases}
          label={q.text}
          name={name}
          rules={[{ required: true }]}
        >
          <Radio.Group className={clasesGrupo(6)}>
            {[1, 2, 3, 4, 5, 6].map((v) => (
              <Radio key={v} value={v}>
                {v}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      );
    }

    if (subtype === "npt_es") {
      return (
        <Form.Item
          key={q.id}
          className={clases}
          label={q.text}
          name={name}
          rules={[{ required: true }]}
        >
          <Radio.Group className={clasesGrupo(4)}>
            {[0, 1, 2, 3].map((v) => (
              <Radio key={v} value={v}>
                {v}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      );
    }

    if (subtype === "rating_1_5") {
      return (
        <Form.Item
          key={q.id}
          className={clases}
          label={q.text}
          name={name}
          rules={[{ required: true }]}
        >
          <Rate count={5} allowClear={false} />
        </Form.Item>
      );
    }

    if (subtype === "checklist") {
      return (
        <Form.Item
          key={q.id}
          className={clases}
          name={name}
          valuePropName="checked"
          label={q.text}
        >
          <Checkbox>{t("respuestas.aplicado")}</Checkbox>
        </Form.Item>
      );
    }

    return (
      <Form.Item
        key={q.id}
        className={clases}
        label={q.text}
        name={name}
        rules={[{ required: true }]}
      >
        {/*
          El techo es POR ITEM: lo trae `maxValue` de la plantilla. Sin el, un
          error de tecleo entraba en la historia clinica como dato valido,
          porque el servidor tampoco acotaba. `inputMode` abre el teclado
          numerico en el movil.
        */}
        <InputNumber
          style={{ width: "100%" }}
          min={0}
          max={Number.isFinite(q.maxValue) ? q.maxValue : undefined}
          inputMode="numeric"
        />
      </Form.Item>
    );
  });
}
